import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Switch, message, Row, Col, Card, Button, InputNumber } from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { agentVariantEnhancedService } from "../../../../api/enhancedApi";
import SupplierSelector from "../../../../components/common/SupplierSelector";
import { STANDARD_COST_TYPE_OPTIONS } from "../../../settlement/constants";

const { Option } = Select;

const AgentVariantRuleForm = ({ visible, mode, initialValues, onCancel, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    // FlyMeta 本地 Agent 标识列表，如 ocr_agent, catering_agent
    const [localAgentNames, setLocalAgentNames] = useState([]);
    // Spidermind 平台的 Agent 名称列表，如 "Mistral OCR Agent", "应付发票结构化Agent_WS"
    // 注意：这与 local_agent_name 不同，是用于路由的目标 Agent
    const [targetAgentCodes, setTargetAgentCodes] = useState([]);

    // 加载名称列表
    useEffect(() => {
        const loadData = async () => {
            try {
                // 获取本地Agent名称（FlyMeta 代码中的 Agent 标识）
                const localResponse = await agentVariantEnhancedService.getLocalAgentNames();
                if (localResponse?.data) {
                    setLocalAgentNames(localResponse.data.items || []);
                }
                // 获取目标Agent编码（Spidermind 平台的 Agent 名称）
                const targetResponse = await agentVariantEnhancedService.getTargetAgentCodes();
                if (targetResponse?.data) {
                    setTargetAgentCodes(targetResponse.data.items || []);
                }
            } catch (error) {
                console.error("加载名称列表失败:", error);
            }
        };

        if (visible) {
            loadData();
        }
    }, [visible]);

    // 表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 处理匹配条件
            let matchConditions = null;
            if (values.matchConditions && values.matchConditions.length > 0) {
                matchConditions = {};
                values.matchConditions.forEach((condition) => {
                    if (condition.key && condition.value !== undefined && condition.value !== null && condition.value !== "") {
                        matchConditions[condition.key] = condition.value;
                    }
                });
                // 如果没有有效条件，设为 null（默认规则）
                if (Object.keys(matchConditions).length === 0) {
                    matchConditions = null;
                }
            }

            const submitData = {
                local_agent_name: values.local_agent_name,
                target_agent_code: values.target_agent_code,
                match_conditions: matchConditions,
                description: values.description,
                priority: values.priority || 0,
                is_active: values.is_active,
            };

            if (mode === "add") {
                const createResponse = await agentVariantEnhancedService.create(submitData);
                if (createResponse.data.success) {
                    message.success(createResponse.data.message || "创建成功");
                    onSuccess();
                } else {
                    throw new Error(createResponse.data.message || "创建失败");
                }
            } else {
                const updateResponse = await agentVariantEnhancedService.update({ id: initialValues.id, data: submitData });
                if (updateResponse.data.success) {
                    message.success(updateResponse.data.message || "更新成功");
                    onSuccess();
                } else {
                    throw new Error(updateResponse.data.message || "更新失败");
                }
            }
        } catch (error) {
            console.error("提交失败:", error);
            message.error(error.response?.data?.detail || error.message || "提交失败");
        } finally {
            setLoading(false);
        }
    };

    // 重置表单
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 初始化表单数据
    useEffect(() => {
        if (visible && initialValues) {
            // 将匹配条件转换为表单格式
            let matchConditions = [];
            if (initialValues.match_conditions) {
                matchConditions = Object.entries(initialValues.match_conditions).map(([key, value]) => {
                    // 推断类型
                    let type = "custom";
                    if (key === "supplier_id") type = "supplier";
                    if (key === "standard_cost_type") type = "standard_cost_type";

                    // 如果key是supplier_id，且value是字符串包含名称，则提取ID
                    let finalValue = value;
                    if (key === "supplier_id" && typeof value === "string") {
                        const parsed = parseInt(value, 10);
                        if (!isNaN(parsed)) {
                            finalValue = parsed;
                        }
                    }

                    return {
                        key,
                        value: finalValue,
                        type,
                    };
                });
            }

            form.setFieldsValue({
                local_agent_name: initialValues.local_agent_name,
                target_agent_code: initialValues.target_agent_code,
                matchConditions: matchConditions.length > 0 ? matchConditions : [{ key: "", value: "", type: "custom" }],
                description: initialValues.description,
                priority: initialValues.priority,
                is_active: initialValues.is_active,
            });
        } else if (visible) {
            form.setFieldsValue({
                is_active: true,
                priority: 0,
                matchConditions: [{ key: "", value: "", type: "custom" }],
            });
        }
    }, [visible, initialValues, form]);

    // 处理条件类型变更
    const handleTypeChange = (index, type) => {
        const conditions = form.getFieldValue("matchConditions");
        const newConditions = [...conditions];

        let newKey = "";
        if (type === "supplier") newKey = "supplier_id";
        if (type === "standard_cost_type") newKey = "standard_cost_type";

        newConditions[index] = {
            ...newConditions[index],
            type,
            key: newKey,
            value: undefined, // 清空值
        };

        form.setFieldsValue({ matchConditions: newConditions });
    };

    return (
        <Modal
            title={mode === "add" ? "创建Agent变体路由规则" : "编辑Agent变体路由规则"}
            open={visible}
            onCancel={handleCancel}
            width={900}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    取消
                </Button>,
                <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
                    {mode === "add" ? "创建" : "更新"}
                </Button>,
            ]}
            className="agent-variant-form-modal"
        >
            <style>
                {`
                    .agent-variant-form-modal .ant-form-item-control {
                        min-width: 100px !important;
                    }
                `}
            </style>
            <Form form={form} layout="vertical">
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="local_agent_name" label="本地Agent名称" rules={[{ required: true, message: "请选择本地Agent名称" }]} tooltip="FlyMeta 代码中的 Agent 标识，如 ocr_agent">
                            <Select placeholder="请选择本地Agent名称" allowClear showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                                {localAgentNames.map((name) => (
                                    <Option key={name} value={name}>
                                        {name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="target_agent_code" label="目标Agent编码" rules={[{ required: true, message: "请选择目标Agent编码" }]} tooltip="Spidermind 平台中的 Agent 名称">
                            <Select placeholder="请选择目标Agent编码" allowClear showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                                {targetAgentCodes.map((code) => (
                                    <Option key={code} value={code}>
                                        {code}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item name="priority" label="优先级" tooltip="数值越大优先级越高，默认为0（最低优先级，通常用于默认规则）">
                            <InputNumber min={0} max={100} style={{ width: "100%" }} placeholder="0-100，数值越大优先级越高" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="is_active" label="启用状态" valuePropName="checked">
                            <Switch checkedChildren="启用" unCheckedChildren="停用" />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="description" label="描述">
                    <Input placeholder="请输入规则描述信息（可选）" />
                </Form.Item>

                <Form.Item label="匹配条件" style={{ marginBottom: 0 }} tooltip="留空表示默认规则，当没有其他规则匹配时生效">
                    <Form.List name="matchConditions">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => {
                                    const type = form.getFieldValue(["matchConditions", name, "type"]) || "custom";

                                    return (
                                        <Card key={key} size="small" style={{ marginBottom: 8, background: "#f9f9f9" }}>
                                            <Row gutter={8} align="middle">
                                                <Col span={6}>
                                                    <Form.Item {...restField} name={[name, "type"]} label="类型" initialValue="custom">
                                                        <Select onChange={(val) => handleTypeChange(name, val)}>
                                                            <Option value="custom">自定义</Option>
                                                            <Option value="supplier">供应商</Option>
                                                            <Option value="standard_cost_type">标准费用类型</Option>
                                                        </Select>
                                                    </Form.Item>
                                                </Col>
                                                <Col span={6}>
                                                    <Form.Item {...restField} name={[name, "key"]} label="条件键">
                                                        <Input placeholder="如: supplier_id" disabled={type !== "custom"} />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={10}>
                                                    <Form.Item {...restField} name={[name, "value"]} label="条件值">
                                                        {type === "supplier" ? (
                                                            <SupplierSelector placeholder="请选择供应商" style={{ minWidth: "240px" }} />
                                                        ) : type === "standard_cost_type" ? (
                                                            <Select placeholder="请选择标准费用类型" options={STANDARD_COST_TYPE_OPTIONS} showSearch optionFilterProp="label" />
                                                        ) : (
                                                            <Input placeholder="如: 123" />
                                                        )}
                                                    </Form.Item>
                                                </Col>
                                                <Col span={2} style={{ display: "flex", alignItems: "center", paddingTop: 30 }}>
                                                    {fields.length > 1 && <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />}
                                                </Col>
                                            </Row>
                                        </Card>
                                    );
                                })}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add({ type: "custom", key: "", value: "" })} block icon={<PlusOutlined />}>
                                        添加条件
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AgentVariantRuleForm;
